import { useEffect, useState } from 'react'
import { Link, useLoaderData } from 'react-router'
import { getEventDetails, getTourDates } from '#app/utils/dmapi.server.ts'
import {
	formatDateUTC,
	isDatePassedUTC,
	isDateReachedUTC,
} from '#app/utils/misc.tsx'
import { type Route } from './+types/nationals._index.ts'

export const meta: Route.MetaFunction = () => {
	const title = 'Nationals 2026 | DanceMakers'
	const description =
		'Join dancers from across the nation for DanceMakers Nationals in Myrtle Beach, SC. 7 days of world-class convention, competition, cash prizes, scholarships, and the prestigious DanceAgain Showcase.'
	const siteUrl = 'https://dancemakersinc.com'
	const ogImage = `${siteUrl}/img/og-image.jpg`

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:image', content: ogImage },
		{ property: 'og:url', content: `${siteUrl}/nationals` },
	]
}

export async function loader() {
	const apiUrl = process.env.DMAPI_URL || 'http://localhost:3001'

	try {
		// Fetch tour dates to find the Nationals event
		const tourData = await getTourDates<TourData>(apiUrl)
		if (!tourData) {
			// Return placeholder data - page will still render
			return { event: null, slug: null }
		}

		// Find the Nationals event using the isNationals flag
		const nationalsEvent = tourData.events.find((e) => e.isNationals)

		if (!nationalsEvent) {
			// Return placeholder data if no nationals event found yet - page will still render
			return { event: null, slug: null }
		}

		// Fetch full event details
		const eventResponse = await getEventDetails<EventData>(
			apiUrl,
			nationalsEvent.id,
		)
		if (!eventResponse.data) {
			return { event: null, slug: nationalsEvent.slug }
		}

		return { event: eventResponse.data, slug: nationalsEvent.slug }
	} catch (error) {
		console.error('Error fetching nationals event:', error)
		// Return placeholder data - page will still render with defaults
		return { event: null, slug: null }
	}
}

// Countdown Timer Component
function CountdownTimer({ targetDate }: { targetDate: string }) {
	const [timeLeft, setTimeLeft] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	})

	useEffect(() => {
		const calculateTimeLeft = () => {
			const difference = new Date(targetDate).getTime() - new Date().getTime()

			if (difference > 0) {
				setTimeLeft({
					days: Math.floor(difference / (1000 * 60 * 60 * 24)),
					hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
					minutes: Math.floor((difference / 1000 / 60) % 60),
					seconds: Math.floor((difference / 1000) % 60),
				})
			}
		}

		calculateTimeLeft()
		const timer = setInterval(calculateTimeLeft, 1000)

		return () => clearInterval(timer)
	}, [targetDate])

	return (
		<div className="flex gap-4 sm:gap-6 md:gap-8">
			<div className="text-center">
				<div className="font-display text-3xl font-bold tabular-nums sm:text-4xl md:text-6xl">
					{String(timeLeft.days).padStart(2, '0')}
				</div>
				<div className="mt-1 text-xs uppercase tracking-wider text-white/70 sm:text-sm">
					Days
				</div>
			</div>
			<div className="text-2xl font-light text-white/50 sm:text-4xl md:text-6xl">
				:
			</div>
			<div className="text-center">
				<div className="font-display text-3xl font-bold tabular-nums sm:text-4xl md:text-6xl">
					{String(timeLeft.hours).padStart(2, '0')}
				</div>
				<div className="mt-1 text-xs uppercase tracking-wider text-white/70 sm:text-sm">
					Hours
				</div>
			</div>
			<div className="text-2xl font-light text-white/50 sm:text-4xl md:text-6xl">
				:
			</div>
			<div className="text-center">
				<div className="font-display text-3xl font-bold tabular-nums sm:text-4xl md:text-6xl">
					{String(timeLeft.minutes).padStart(2, '0')}
				</div>
				<div className="mt-1 text-xs uppercase tracking-wider text-white/70 sm:text-sm">
					Min
				</div>
			</div>
			<div className="hidden text-2xl font-light text-white/50 sm:block sm:text-4xl md:text-6xl">
				:
			</div>
			<div className="hidden text-center sm:block">
				<div className="font-display text-3xl font-bold tabular-nums sm:text-4xl md:text-6xl">
					{String(timeLeft.seconds).padStart(2, '0')}
				</div>
				<div className="mt-1 text-xs uppercase tracking-wider text-white/70 sm:text-sm">
					Sec
				</div>
			</div>
		</div>
	)
}

// Feature Card Component for Why Nationals section
function FeatureCard({
	icon,
	title,
	description,
}: {
	icon: React.ReactNode
	title: string
	description: string
}) {
	return (
		<div className="group border border-muted-foreground/30 bg-background/50 p-6 backdrop-blur-sm transition-all duration-300 hover:border-primary/50 sm:p-8">
			<div className="mb-4 flex h-12 w-12 items-center justify-center text-primary transition-transform duration-300 group-hover:scale-110 sm:mb-6 sm:h-14 sm:w-14">
				{icon}
			</div>
			<h3 className="mb-2 font-display text-lg font-bold uppercase tracking-wide sm:mb-3 sm:text-xl">
				{title}
			</h3>
			<p className="text-sm leading-relaxed text-muted-foreground">
				{description}
			</p>
		</div>
	)
}

// Quick Link Card Component
function QuickLinkCard({
	to,
	title,
	description,
}: {
	to: string
	title: string
	description: string
}) {
	return (
		<Link
			to={to}
			className="group border border-muted-foreground/30 p-6 transition-all duration-300 hover:border-primary/50 hover:bg-accent/5"
		>
			<div className="flex items-start justify-between gap-4">
				<div>
					<h3 className="mb-2 font-display font-bold uppercase tracking-wide transition-colors group-hover:text-primary">
						{title}
					</h3>
					<p className="text-xs leading-relaxed text-muted-foreground">
						{description}
					</p>
				</div>
				<svg
					className="mt-1 h-5 w-5 flex-shrink-0 text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-primary"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M9 5l7 7-7 7"
					/>
				</svg>
			</div>
		</Link>
	)
}

export default function NationalsIndex() {
	const { event, slug } = useLoaderData<typeof loader>()

	// Default to June 2026 if no event data
	const eventStartDate = event?.startDate || '2026-06-22T00:00:00.000Z'
	const registrationOpensDate = event?.opensDate || '2026-03-05T00:00:00.000Z'
	const registrationOpen =
		isDatePassedUTC(registrationOpensDate) ||
		isDateReachedUTC(registrationOpensDate)
	const registrationClosed = isDatePassedUTC(event?.cutoff)

	// Format registration opens date for display
	const formatRegistrationOpensDate = () => {
		const date = new Date(registrationOpensDate)
		return date.toLocaleDateString('en-US', {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
			timeZone: 'UTC',
		})
	}

	// Format date range for display
	const formatEventDates = () => {
		if (!event?.startDate) return 'June 22-28, 2026'

		const start = new Date(event.startDate)
		const end = event.endDate ? new Date(event.endDate) : null

		const startMonth = start.toLocaleDateString('en-US', {
			month: 'long',
			timeZone: 'UTC',
		})
		const startDay = start.toLocaleDateString('en-US', {
			day: 'numeric',
			timeZone: 'UTC',
		})
		const startYear = start.toLocaleDateString('en-US', {
			year: 'numeric',
			timeZone: 'UTC',
		})

		if (!end) return `${startMonth} ${startDay}, ${startYear}`

		const endDay = end.toLocaleDateString('en-US', {
			day: 'numeric',
			timeZone: 'UTC',
		})
		const endMonth = end.toLocaleDateString('en-US', {
			month: 'long',
			timeZone: 'UTC',
		})

		if (startMonth === endMonth) {
			return `${startMonth} ${startDay}-${endDay}, ${startYear}`
		}
		return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`
	}

	return (
		<div className="min-h-screen">
			{/* ===== HERO SECTION ===== */}
			<section className="relative flex min-h-screen items-center justify-center overflow-hidden">
				{/* Video Background */}
				<div className="absolute inset-0 z-0">
					<video
						src="/assets/video/tour-banner.mp4"
						autoPlay
						loop
						muted
						playsInline
						poster="/assets/video/tour-banner.jpg"
						className="h-full w-full object-cover"
					/>
					{/* Gradient Overlay */}
					<div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
				</div>

				{/* Hero Content */}
				<div className="relative z-10 px-6 py-20 text-center text-white">
					<p className="mb-4 animate-fade-in text-xs font-medium uppercase tracking-[0.3em] text-accent sm:mb-6 sm:text-sm">
						The Ultimate Dance Experience
					</p>

					<h1 className="mb-4 font-display text-5xl font-bold uppercase tracking-tight sm:text-6xl md:text-7xl lg:text-8xl">
						Nationals
						<span className="mt-2 block text-3xl text-accent sm:text-4xl md:text-5xl lg:text-6xl">
							2026
						</span>
					</h1>

					<p className="mb-2 text-lg font-light text-white/80 sm:text-xl md:text-2xl">
						Myrtle Beach, SC
					</p>
					<p className="mb-8 text-base text-white/60 sm:mb-12 sm:text-lg">
						{formatEventDates()}
					</p>

					{/* Countdown Timer */}
					<div className="mb-10 flex justify-center sm:mb-14">
						<CountdownTimer targetDate={eventStartDate} />
					</div>

					{/* CTA Buttons */}
					<div className="flex flex-col justify-center gap-4 sm:flex-row">
						{registrationClosed ? (
							<span className="rounded-none border border-white/30 px-8 py-4 text-sm font-medium uppercase tracking-wider text-white/50">
								Registration Closed
							</span>
						) : registrationOpen ? (
							<a
								href="https://dancemakersinc.io"
								target="_blank"
								rel="noopener noreferrer"
								className="bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90"
							>
								Register Now
							</a>
						) : (
							<span className="rounded-none border border-white/50 px-8 py-4 text-sm font-medium uppercase tracking-wider text-white">
								Registration Opens {formatRegistrationOpensDate()}
							</span>
						)}
						<a
							href="#why-nationals"
							className="border border-white/50 px-8 py-4 text-sm font-medium uppercase tracking-wider text-white transition-colors hover:bg-white/10"
						>
							Learn More
						</a>
					</div>
				</div>

				{/* Scroll Indicator */}
				<div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 animate-bounce">
					<svg
						className="h-6 w-6 text-white/50"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M19 14l-7 7m0 0l-7-7m7 7V3"
						/>
					</svg>
				</div>
			</section>

			{/* ===== PHOTO SHOWCASE SECTION ===== */}
			<section className="overflow-hidden py-16 sm:py-20 md:py-28">
				<div className="container px-6 md:px-16">
					{/* Section Header */}
					<div className="mb-12 text-center sm:mb-16">
						<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
							Nationals Myrtle Beach 2026
						</p>
						<h2 className="mb-4 font-display text-3xl font-bold uppercase sm:text-4xl md:text-5xl">
							We Want to Experience It With YOU!
						</h2>
						<p className="mx-auto max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
							Scholarships, awards, special guests, more classes, dance parties,
							teacher cocktail parties, two massive stages, DanceAgain Showcase,
							The Collective, a huge Wrap It ceremony and
							<span className="font-medium text-foreground">
								{' '}
								so much more!
							</span>
						</p>
					</div>

					{/* Photo Grid */}
					<div className="mb-16 grid grid-cols-2 gap-2 sm:mb-20 sm:gap-3 md:grid-cols-4">
						{/* Large feature image */}
						<div className="group relative col-span-2 row-span-2 overflow-hidden">
							<img
								src="/img/nationals/1.png"
								alt="Nationals dance performance"
								className="aspect-square h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
							<div className="absolute bottom-4 left-4 right-4">
								<p className="font-display text-lg font-bold uppercase text-white sm:text-xl">
									Epic Wrap It Ceremony
								</p>
							</div>
						</div>

						{/* Smaller images */}
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/2.png"
								alt="Nationals moment"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/3.png"
								alt="Scholarships & Awards"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
							<div className="absolute bottom-2 left-2 right-2">
								<p className="font-display text-xs font-bold uppercase text-white sm:text-sm">
									Scholarships & Awards
								</p>
							</div>
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/4.png"
								alt="The Collective"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
							<div className="absolute bottom-2 left-2 right-2">
								<p className="font-display text-xs font-bold uppercase text-white sm:text-sm">
									The Collective
								</p>
							</div>
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/5.png"
								alt="Nationals moment"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
						</div>

						{/* Wide image spanning 2 columns */}
						<div className="group relative col-span-2 aspect-[2/1] overflow-hidden">
							<img
								src="/img/nationals/6.png"
								alt="Nationals showcase"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
							<div className="absolute bottom-4 left-4 right-4">
								<p className="font-display text-lg font-bold uppercase text-white sm:text-xl">
									Dance Parties
								</p>
							</div>
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/7.png"
								alt="DanceAgain Showcase"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
							<div className="absolute bottom-2 left-2 right-2">
								<p className="font-display text-xs font-bold uppercase text-white sm:text-sm">
									DanceAgain Showcase
								</p>
							</div>
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/8.png"
								alt="Nationals moment"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
						</div>

						{/* Additional row with remaining images */}
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/9.png"
								alt="Nationals moment"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/10.png"
								alt="2 Epic Stages"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
							<div className="absolute bottom-2 left-2 right-2">
								<p className="font-display text-xs font-bold uppercase text-white sm:text-sm">
									2 Epic Stages
								</p>
							</div>
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/11.png"
								alt="Nationals moment"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
						</div>
						<div className="group relative aspect-square overflow-hidden">
							<img
								src="/img/nationals/12.png"
								alt="Nationals moment"
								className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
							/>
							<div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
						</div>
					</div>
				</div>
			</section>

			{/* ===== WHY NATIONALS SECTION ===== */}
			<section
				id="why-nationals"
				className="bg-muted/30 py-16 sm:py-20 md:py-28"
			>
				<div className="container px-6 md:px-16">
					<div className="mb-12 text-center sm:mb-16">
						<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
							Experience the Difference
						</p>
						<h2 className="font-display text-3xl font-bold uppercase sm:text-4xl md:text-5xl">
							Why Nationals?
						</h2>
					</div>

					<div className="mx-auto grid max-w-7xl gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
						<FeatureCard
							icon={
								<svg
									className="h-8 w-8 sm:h-10 sm:w-10"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
									/>
								</svg>
							}
							title="A week of dance"
							description="An entire week of world-class convention classes, competition, and unforgettable experiences. More time to learn, grow, and connect."
						/>

						<FeatureCard
							icon={
								<svg
									className="h-8 w-8 sm:h-10 sm:w-10"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
									/>
								</svg>
							}
							title="Dancers Nationwide"
							description="Dancers from 40+ states come together to compete and train. Experience the energy of the nation's best all in one place."
						/>

						<FeatureCard
							icon={
								<svg
									className="h-8 w-8 sm:h-10 sm:w-10"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
							}
							title="Cash Prizes"
							description="Compete for cash prizes awarded to top overall winners in each age division and category. The stakes are higher at Nationals."
						/>

						<FeatureCard
							icon={
								<svg
									className="h-8 w-8 sm:h-10 sm:w-10"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
									/>
								</svg>
							}
							title="DanceAgain Showcase"
							description="Top overall winners are invited to perform at the prestigious DanceAgain Showcase, featuring the best talent from across the country."
						/>
					</div>

					<div className="mx-auto mt-4 grid max-w-5xl gap-4 sm:mt-6 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
						<FeatureCard
							icon={
								<svg
									className="h-8 w-8 sm:h-10 sm:w-10"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
									/>
								</svg>
							}
							title="Elite Scholarships"
							description="Compete for scholarships including opportunities with Pace University and The Collective. Your future starts here."
						/>

						<FeatureCard
							icon={
								<svg
									className="h-8 w-8 sm:h-10 sm:w-10"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
									/>
								</svg>
							}
							title="World-Class Faculty"
							description="Train with the best in the industry. Our faculty brings expertise from Broadway, professional companies, and top studios nationwide."
						/>

						<FeatureCard
							icon={
								<svg
									className="h-8 w-8 sm:h-10 sm:w-10"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									strokeWidth={1.5}
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
									/>
								</svg>
							}
							title="Unforgettable Memories"
							description="Make lifelong friends, celebrate achievements, and create memories that will last forever. This is more than a competition."
						/>
					</div>
				</div>
			</section>

			{/* ===== EVENT DETAILS SECTION ===== */}
			<section className="py-16 sm:py-20 md:py-28">
				<div className="container px-6 md:px-16">
					<div className="mb-12 text-center sm:mb-16">
						<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
							Mark Your Calendar
						</p>
						<h2 className="font-display text-3xl font-bold uppercase sm:text-4xl md:text-5xl">
							Event Details
						</h2>
					</div>

					<div className="mx-auto max-w-5xl">
						<div className="grid gap-6 sm:gap-8 md:grid-cols-2">
							{/* Dates Card */}
							<div className="border border-muted-foreground/30 bg-muted/20 p-6 sm:p-8">
								<div className="mb-6 flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center text-primary">
										<svg
											className="h-6 w-6"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={2}
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
											/>
										</svg>
									</div>
									<h3 className="font-display text-lg font-bold uppercase tracking-wide sm:text-xl">
										Event Dates
									</h3>
								</div>
								<div className="space-y-4 text-sm">
									<div className="flex items-center justify-between border-b border-border/50 py-3">
										<span className="font-display font-bold tracking-wide">
											Nationals Week
										</span>
										<span className="font-bold text-primary">
											{formatEventDates()}
										</span>
									</div>
									{event?.opensDate && (
										<div className="flex items-center justify-between border-b border-border/50 py-3">
											<span className="font-display tracking-wide">
												Registration Opens
											</span>
											<span className="font-bold">
												{formatDateUTC(event.opensDate, {
													month: 'long',
													day: 'numeric',
													year: 'numeric',
												})}
											</span>
										</div>
									)}
									{event?.cutoff && (
										<div className="flex items-center justify-between border-b border-border/50 py-3">
											<span className="font-display tracking-wide">
												Registration Cutoff
											</span>
											<span
												className={`font-bold ${isDatePassedUTC(event.cutoff) ? 'text-muted-foreground line-through' : ''}`}
											>
												{formatDateUTC(event.cutoff, {
													month: 'long',
													day: 'numeric',
													year: 'numeric',
												})}
											</span>
										</div>
									)}
									{event?.scheduleRelease && (
										<div className="flex items-center justify-between py-3">
											<span className="font-display tracking-wide">
												Schedule Release
											</span>
											<span className="font-bold">
												{formatDateUTC(event.scheduleRelease, {
													month: 'long',
													day: 'numeric',
													year: 'numeric',
												})}
											</span>
										</div>
									)}
								</div>
							</div>

							{/* Venue Card */}
							<div className="border border-muted-foreground/30 bg-muted/20 p-6 sm:p-8">
								<div className="mb-6 flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center text-primary">
										<svg
											className="h-6 w-6"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={2}
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
											/>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
											/>
										</svg>
									</div>
									<h3 className="font-display text-lg font-bold uppercase tracking-wide sm:text-xl">
										Location
									</h3>
								</div>
								<div className="space-y-4">
									{event?.venue ? (
										<>
											<div>
												<h4 className="mb-1 font-display font-bold tracking-wide">
													{event.venue.name}
												</h4>
												<p className="text-sm text-muted-foreground">
													{event.venue.address}
													<br />
													{event.venue.city}, {event.venue.state}{' '}
													{event.venue.zip}
												</p>
											</div>
											<a
												href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${event.venue.name} ${event.venue.address} ${event.venue.city} ${event.venue.state} ${event.venue.zip}`)}`}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-2 bg-accent px-4 py-2 text-xs font-semibold transition-colors hover:bg-accent/90"
											>
												Get Directions
												<svg
													className="h-3 w-3"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
													/>
												</svg>
											</a>
										</>
									) : (
										<>
											<div>
												<h4 className="mb-1 font-display font-bold tracking-wide">
													Myrtle Beach Convention Center
												</h4>
												<p className="text-sm text-muted-foreground">
													Myrtle Beach, SC
												</p>
											</div>
											<p className="text-xs italic text-muted-foreground">
												Full venue details coming soon
											</p>
										</>
									)}
								</div>
							</div>

							{/* Host Hotel Card */}
							{event?.hostHotels && event.hostHotels.length > 0 ? (
								event.hostHotels.map((hotel, index) => (
									<div
										key={hotel.id}
										className="border border-muted-foreground/30 bg-muted/20 p-6 sm:p-8 md:col-span-2"
									>
										<div className="mb-6 flex items-center gap-3">
											<div className="flex h-10 w-10 items-center justify-center text-primary">
												<svg
													className="h-6 w-6"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
													strokeWidth={2}
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
													/>
												</svg>
											</div>
											<h3 className="font-display text-lg font-bold uppercase tracking-wide sm:text-xl">
												{index === 0 ? 'Host Hotel' : 'Overflow Hotel'}
											</h3>
										</div>
										<div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
											<div className="space-y-2">
												<h4 className="font-display font-bold tracking-wide">
													{hotel.name}
												</h4>
												<p className="text-sm text-muted-foreground">
													{hotel.address}
													<br />
													{hotel.city}, {hotel.state} {hotel.zip}
												</p>
												{hotel.phone && (
													<p className="text-sm text-muted-foreground">
														{hotel.phone}
													</p>
												)}
											</div>
											{(hotel.rate || hotel.cutOff) && (
												<div className="flex flex-wrap gap-3">
													{hotel.cutOff && (
														<div className="bg-muted/50 p-3">
															<span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-primary">
																Block Cutoff
															</span>
															<span className="text-sm font-bold">
																{formatDateUTC(hotel.cutOff, {
																	month: 'short',
																	day: 'numeric',
																	year: 'numeric',
																})}
															</span>
														</div>
													)}
													{hotel.rate && (
														<div className="bg-muted/50 p-3">
															<span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-primary">
																Rate
															</span>
															<span className="text-sm font-bold">
																{hotel.rate}
															</span>
														</div>
													)}
												</div>
											)}
										</div>
										<div className="mt-6 flex flex-wrap gap-2">
											{hotel.block && (
												<a
													href={hotel.block}
													target="_blank"
													rel="noopener noreferrer"
													className="inline-flex items-center gap-2 bg-accent px-4 py-2 text-xs font-semibold transition-colors hover:bg-accent/90"
												>
													Book Room Block
													<svg
														className="h-3 w-3"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
														/>
													</svg>
												</a>
											)}
											{hotel.website && (
												<a
													href={hotel.website}
													target="_blank"
													rel="noopener noreferrer"
													className="inline-flex items-center gap-2 bg-muted px-4 py-2 text-xs font-semibold transition-colors hover:bg-muted/80"
												>
													Hotel Website
													<svg
														className="h-3 w-3"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
														/>
													</svg>
												</a>
											)}
										</div>
									</div>
								))
							) : (
								<div className="border border-muted-foreground/30 bg-muted/20 p-6 sm:p-8 md:col-span-2">
									<div className="mb-6 flex items-center gap-3">
										<div className="flex h-10 w-10 items-center justify-center text-primary">
											<svg
												className="h-6 w-6"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
												strokeWidth={2}
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
												/>
											</svg>
										</div>
										<h3 className="font-display text-lg font-bold uppercase tracking-wide sm:text-xl">
											Host Hotel
										</h3>
									</div>
									<p className="text-sm text-muted-foreground">
										Host hotel information and room block details will be
										announced soon. Check back for booking links and special
										rates.
									</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</section>

			{/* ===== QUICK LINKS SECTION ===== */}
			<section className="bg-muted/30 py-16 sm:py-20 md:py-28">
				<div className="container px-6 md:px-16">
					<div className="mb-12 text-center sm:mb-16">
						<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
							Everything You Need to Know
						</p>
						<h2 className="font-display text-3xl font-bold uppercase sm:text-4xl md:text-5xl">
							Nationals Info
						</h2>
					</div>

					<div className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
						<QuickLinkCard
							to="/nationals/convention"
							title="Convention Guidelines"
							description="Ages, fees, registration details, wristbands, ballroom guidelines, and everything you need for convention."
						/>
						<QuickLinkCard
							to="/nationals/competition"
							title="Competition Guidelines"
							description="Fees, time limits, age divisions, performance styles, judging criteria, and competition rules."
						/>
						<QuickLinkCard
							to="/nationals/awards"
							title="Awards & Cash Prizes"
							description="Point system, score levels, cash prizes, DanceAgain Showcase, and studio recognition awards."
						/>
						<QuickLinkCard
							to="/nationals/scholarships"
							title="Scholarships"
							description="Regional scholarships, Pace University opportunities, The Collective, and senior audition information."
						/>
						<QuickLinkCard
							to="/nationals/schedule"
							title="Sample Schedule"
							description="Get a preview of what a typical day at Nationals looks like with our sample convention and event schedule."
						/>
						{slug && (
							<QuickLinkCard
								to={`/tour/${slug}`}
								title="Full Event Details"
								description="View the complete event page with schedule, livestream, results, and more."
							/>
						)}
					</div>
				</div>
			</section>

			{/* ===== CTA SECTION ===== */}
			<section className="relative overflow-hidden py-20 sm:py-28 md:py-36">
				{/* Background */}
				<div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-primary/5" />

				<div className="container relative z-10 px-6 md:px-16">
					<div className="mx-auto max-w-3xl text-center">
						<h2 className="mb-6 font-display text-3xl font-bold uppercase sm:text-4xl md:text-5xl">
							Don't Miss the Biggest Event of the Year
						</h2>
						<p className="mx-auto mb-10 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
							Join dancers from across the nation for an unforgettable week of
							world-class training, intense competition, and memories that will
							last a lifetime. Nationals is where champions are made.
						</p>

						<div className="flex flex-col justify-center gap-4 sm:flex-row">
							{registrationClosed ? (
								<span className="border border-muted-foreground/30 px-8 py-4 text-sm font-medium uppercase tracking-wider text-muted-foreground">
									Registration Closed
								</span>
							) : registrationOpen ? (
								<a
									href="https://dancemakersinc.io"
									target="_blank"
									rel="noopener noreferrer"
									className="bg-primary px-10 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90"
								>
									Register for Nationals
								</a>
							) : (
								<span className="border border-primary/50 px-8 py-4 text-sm font-medium uppercase tracking-wider text-primary">
									Registration Opens {formatRegistrationOpensDate()}
								</span>
							)}
							<Link
								to="/contact"
								className="border border-muted-foreground/50 px-8 py-4 text-sm font-medium uppercase tracking-wider transition-colors hover:bg-muted/50"
							>
								Contact Us
							</Link>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}

// Type definitions
type HostHotel = {
	id: string
	name: string
	address: string
	city: string
	state: string
	zip: string
	phone: string | null
	website: string | null
	directions: string | null
	rate: string | null
	cutOff: string | null
	block: string | null
}

type Venue = {
	id: string
	name: string
	address: string
	city: string
	state: string
	zip: string
}

type EventData = {
	id: string
	name: string
	slug: string
	dates: string
	startDate: string | null
	endDate: string | null
	opensDate: string | null
	opensTime: string | null
	cutoff: string | null
	scheduleRelease: string | null
	blockOpensDate: string | null
	isNationals: boolean
	limitations: {
		limitText: string | null
		limit: number | null
		studioLimit: number | null
		soloLimit: number | null
	}
	hostHotels: HostHotel[]
	venue: Venue | null
}

type TourEvent = {
	id: string
	name: string
	slug: string
	dates: string
	startDate: string | null
	endDate: string | null
	venue: string | null
	cutoff: string | null
	isNationals?: boolean
}

type TourData = {
	tour: { id: string; name: string } | null
	events: TourEvent[]
}
